import React from "react";
import {
  AuthOnFile,
  TestLocationSubmissionDetails,
} from "../../../../../data-model/medicalPaOrdersModel";
import { InfoIcon } from "../../../../../svg/info-icon";
import { PencilIcon } from "../../../../../svg/pencil-Icon";
import { useSiteNpiValidated } from "../../../hooks/useSiteNpiValidated";
import { getAliasFromAuthOnFileReport } from "../../../utils/formatSiteNpiAliasesForDisplay";

/** Props for the site NPI header segment. */
interface SiteNpiDetailsProps {
  /** Invoked when the edit (pencil) affordance is clicked. */
  onEditSiteNpiClick?: () => void;
  /** Site NPI details. */
  siteNpiDetails?: TestLocationSubmissionDetails;
  /** Auth-on-file payload used to fall back to the first report's test location. */
  authOnFileDetails?: AuthOnFile;
}

/**
 * Renders the site NPI segment of the authorization header.
 *
 * Prefers the submission NPI when present; otherwise shows the first
 * auth-on-file report's ``test_location`` (or a missing-location message).
 *
 * @param props - Edit handler, submission site NPI details, and optional auth-on-file.
 * @returns The site NPI header segment.
 */
const SiteNpiDetails: React.FC<SiteNpiDetailsProps> = ({
  onEditSiteNpiClick,
  siteNpiDetails,
  authOnFileDetails,
}) => {
  const isNpiValidated = useSiteNpiValidated();
  return (
    <div className="flex shrink-0 items-center gap-1 border-l border-primaryGray-14 pl-2 text-sm">
      {isNpiValidated.isValidationNeeded ? (
        <InfoIcon
          width="16px"
          height="16px"
          stroke="#cd0202"
          className="shrink-0"
        />
      ) : null}
      <span>Site NPI: </span>
      <span className="text-h12 font-normal leading-h12 text-primaryGray-6">
        {siteNpiDetails?.npi || getAliasFromAuthOnFileReport(authOnFileDetails)}
      </span>
      <PencilIcon
        width={12}
        height={12}
        onClick={onEditSiteNpiClick}
        aria-label="Edit Site NPI"
        className="cursor-pointer transition-transform hover:scale-110"
      />
    </div>
  );
};

export default SiteNpiDetails;
